// https://dev.to/esponges/indexeddb-your-offline-and-serverless-db-in-your-browser-with-react-3hm7

import { GARIPALLI_IDB_CONFIGS } from "../extensions/idb";

export type IndexedDBFullKey = IDBValidKey | IDBKeyRange | null | undefined;

export interface IndexedDbObjectStoreIndex {
  name: string;
  keyPath: string | string[];
  options?: IDBIndexParameters;
}

export type IndexedDbObjectStoreInitParams<S> = {
  name: keyof S & string;
  keyPath: string | string[] | null;
  autoincrement?: boolean;
  indexes?: IndexedDbObjectStoreIndex[];
};

export const initIDB = async <S>(
  dbName: string,
  version?: number,
  objectsStore?: IndexedDbObjectStoreInitParams<S>[]
): Promise<IDBDatabase | null> => {
  return new Promise((resolve) => {
    let db: IDBDatabase;
    // open the connection
    const request = indexedDB.open(dbName, version);

    request.onupgradeneeded = () => {
      db = request.result;

      console.log("request.onupgradeneeded - initDB", db.version, objectsStore);
      objectsStore?.forEach((object) => {
        if (!db.objectStoreNames.contains(object.name)) {
          console.log(`Creating ${object.name} store`);
          const objectStore = db.createObjectStore(object.name, {
            keyPath: object.keyPath,
            autoIncrement: object.autoincrement ?? true,
          });

          object.indexes?.forEach((index) => {
            console.log(`Creating index ${index.name} on ${object.name} store`);
            try {
              const createdIndex = objectStore.createIndex(
                index.name,
                index.keyPath,
                index.options
              );
              console.log(
                `Created index ${createdIndex.name} on ${object.name} store`
              );
            } catch (error) {
              console.error(
                `Error creating index ${index.name} on ${object.name} store:`,
                error
              );
            }
          });
        } else {
          console.log(`${object.name} store already exists - updating indexes`);
          const objectStore = request.transaction?.objectStore(object.name);
          if (!objectStore) {
            throw new Error(
              `Object store ${object.name} not found during upgrade`
            );
          }

          for (let indexName of objectStore.indexNames) {
            console.log(`Deleting index ${indexName} on ${object.name} store`);
            objectStore.deleteIndex(indexName);
          }

          for (let index of object.indexes || []) {
            console.log(`Creating index ${index.name} on ${object.name} store`);
            const idbIndex = objectStore.createIndex(
              index.name,
              index.keyPath,
              index.options
            );
            console.log(
              `Created index ${idbIndex.name} on ${object.name} store`
            );
          }
        }
      });
    };

    const onSuccess = (e: Event) => {
      cleanUp();
      db = request.result;
      version = db.version;
      console.log("request.onsuccess - initDB", version);

      resolve(db);
    };

    const onError = (e: Event) => {
      cleanUp();
      console.error("request.onerror - initDB", request.error);
      resolve(null);
    };

    const cleanUp = () => {
      request.removeEventListener("success", onSuccess);
      request.removeEventListener("error", onError);
    };

    request.addEventListener("success", onSuccess);
    request.addEventListener("error", onError);
  });
};

export const initIDBWithTimeout = async (
  configs: typeof GARIPALLI_IDB_CONFIGS,
  timeout: number
): Promise<IDBDatabase | null> => {
  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(
      () => reject(new Error(`initIDB timeout after ${timeout}ms`)),
      timeout
    )
  );

  const initPromise = initIDB(
    configs.name,
    configs.version,
    configs.objectsStores
  );

  return Promise.race([
    initPromise,
    timeoutPromise,
  ]) as Promise<IDBDatabase | null>;
};

export function addToIDBStore<T>(
  db: IDBDatabase,
  storeName: string,
  ...data: T[]
): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    data.forEach((item) => store.add(item));

    transaction.oncomplete = () => resolve();

    transaction.onerror = (e) => {
      console.error("IndexedDB add error:", e);
      reject(new Error("Failed to add data"));
    };
  });
}

export function bulkAddToIDBStore<T>(
  db: IDBDatabase,
  storeName: string,
  data: T[]
): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    data.forEach((item) => store.add(item));
    transaction.oncomplete = () => resolve();
    transaction.onerror = (e) => {
      console.error("IndexedDB bulk add error:", e);
      reject(new Error("Failed to bulk add data"));
    };
  });
}

export function getAllIDBStoreRecords<T>(
  db: IDBDatabase,
  storeName: string,
  key?: IndexedDBFullKey,
  count?: number
): Promise<T[]> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, "readonly");
    const store = transaction.objectStore(storeName);
    const request = store.getAll(key, count);
    request.onsuccess = () => {
      resolve(request.result as T[]);
    };

    request.onerror = (e) => {
      console.error("IndexedDB getAll error:", e);
      reject(new Error("Failed to retrieve data"));
    };
  });
}

export function getIDBStoreItemsCount(
  db: IDBDatabase,
  storeName: string
): Promise<number> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, "readonly");
    const store = transaction.objectStore(storeName);
    const request = store.count();
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = (e) => {
      console.error("IndexedDB count error:", e);
      reject(new Error("Failed to count records"));
    };
  });
}

export function clearIDBStore(
  db: IDBDatabase,
  storeName: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    const request = store.clear();
    request.onsuccess = () => {
      resolve();
    };
    request.onerror = (e) => {
      console.error("IndexedDB clear error:", e);
      reject(new Error("Failed to clear records"));
    };
  });
}

export function getIDBStoreItemByKey<T>(
  db: IDBDatabase,
  storeName: string,
  key: IDBValidKey
): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, "readonly");
    const store = transaction.objectStore(storeName);
    const request = store.get(key);
    request.onsuccess = () => {
      resolve(request.result as T | undefined);
    };
    request.onerror = (e) => {
      console.error("IndexedDB get by key error:", e);
      reject(new Error("Failed to get record by key"));
    };
  });
}

export function deleteIDBStoreItem(
  db: IDBDatabase,
  storeName: string,
  key: IDBValidKey
): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    const request = store.delete(key);
    request.onsuccess = () => {
      resolve();
    };
    request.onerror = (e) => {
      console.error("IndexedDB delete error:", e);
      reject(new Error("Failed to delete record"));
    };
  });
}

export function putIDBStoreItem<T>(
  db: IDBDatabase,
  storeName: string,
  ...data: T[]
): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    data.forEach((item) => store.put(item));
    transaction.oncomplete = () => resolve();
    transaction.onerror = (e) => {
      console.error("IndexedDB put error:", e);
      reject(new Error("Failed to put record"));
    };
  });
}

export function bulkPutIDBStoreItems<T>(
  db: IDBDatabase,
  storeName: string,
  data: T[]
): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    data.forEach((item) => store.put(item));
    transaction.oncomplete = () => resolve();
    transaction.onerror = (e) => {
      console.error("IndexedDB bulk put error:", e);
      reject(new Error("Failed to bulk put records"));
    };
  });
}

export function updateIDBStoreItem<T>(
  db: IDBDatabase,
  storeName: string,
  key: IDBValidKey,
  fn: (data: T) => T
): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    const getRequest = store.get(key);
    getRequest.onsuccess = () => {
      const data = getRequest.result as T;
      if (data) {
        const updatedData = fn(data);
        const putRequest = store.put(updatedData);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = (e) => {
          console.error("IndexedDB update put error:", e);
          reject(new Error("Failed to update record"));
        };
      } else {
        reject(new Error("Record not found"));
      }
    };
    getRequest.onerror = (e) => {
      console.error("IndexedDB update get error:", e);
      reject(new Error("Failed to retrieve record for update"));
    };
  });
}

export interface IDBIndexHandler<T> {
  getInstance: () => IDBIndex;
  getAll: (key?: IDBValidKey) => Promise<T[]>;
  get: (key: IDBValidKey) => Promise<T | undefined>;
  count: (key?: IDBValidKey) => Promise<number>;
  openCursor: (
    key?: IDBValidKey,
    direction?: IDBCursorDirection
  ) => Promise<IDBCursorWithValue | null>;
  first: (key?: IDBValidKey | IDBKeyRange | null) => Promise<T | undefined>;
  last: (key?: IDBValidKey | IDBKeyRange | null) => Promise<T | undefined>;
  iterate: (
    callback: (item: T) => void,
    key?: IDBValidKey | IDBKeyRange | null,
    direction?: IDBCursorDirection
  ) => Promise<void>;
}

  export interface IDBStorageObjectHandler<T> {
  get: (id: IDBValidKey) => Promise<T | undefined>;
  getAll: (
    key?: IDBValidKey | IDBKeyRange | null,
    count?: number
  ) => Promise<T[]>;
  add: (...data: T[]) => Promise<void>;
  bulkAdd: (data: T[]) => Promise<void>;
  put: (...data: T[]) => Promise<void>;
  bulkPut: (data: T[]) => Promise<void>;
  count: () => Promise<number>;
  clear: () => Promise<void>;
  delete: (id: IDBValidKey) => Promise<void>;
  update: (id: IDBValidKey, fn: (data: T) => T) => Promise<void>;
  index: (indexName: string, mode: IDBTransactionMode) => IDBIndexHandler<T>;
}

export interface IDBHandler<S> {
  db: IDBDatabase | null;
  close: () => void;
  get: <K extends keyof S>(
    storeName: K,
    id: IDBValidKey
  ) => Promise<S[K] | undefined>;
  getAll: <K extends keyof S>(
    storeName: K,
    key?: IndexedDBFullKey,
    count?: number
  ) => Promise<S[K][]>;
  add: <K extends keyof S>(storeName: K, ...data: S[K][]) => Promise<void>;
  count: <K extends keyof S>(storeName: K) => Promise<number>;
  objectStore: <K extends keyof S>(
    storeName: K
  ) => IDBStorageObjectHandler<S[K]>;
  put: <K extends keyof S>(storeName: K, ...data: S[K][]) => Promise<void>;
  delete: <K extends keyof S>(storeName: K, id: IDBValidKey) => Promise<void>;
  update: <K extends keyof S>(
    storeName: K,
    id: IDBValidKey,
    fn: (data: S[K]) => S[K]
  ) => Promise<void>;
  clear: <K extends keyof S>(storeName: K) => Promise<void>;
  bulkAdd: <K extends keyof S>(storeName: K, data: S[K][]) => Promise<void>;
  bulkPut: <K extends keyof S>(storeName: K, data: S[K][]) => Promise<void>;
}

function createIDBStorageIndexHandler<T>(index: IDBIndex): IDBIndexHandler<T> {
  return {
    getInstance: () => index,
    getAll: (key?: IndexedDBFullKey, count?: number) => {
      return new Promise<T[]>((resolve, reject) => {
        const request = index.getAll(key, count);
        request.onsuccess = () => resolve(request.result);
        request.onerror = (e) => {
          console.error("IndexedDB index getAll error:", e);
          reject(new Error("Failed to getAll from index"));
        };
      });
    },
    get: (key: IDBValidKey | IDBKeyRange) => {
      return new Promise<T | undefined>((resolve, reject) => {
        const request = index.get(key);
        request.onsuccess = () => resolve(request.result);
        request.onerror = (e) => {
          console.error("IndexedDB index get error:", e);
          reject(new Error("Failed to get from index"));
        };
      });
    },
    count: (key?: IDBValidKey | IDBKeyRange) => {
      return new Promise<number>((resolve, reject) => {
        const request = key ? index.count(key) : index.count();
        request.onsuccess = () => resolve(request.result);
        request.onerror = (e) => {
          console.error("IndexedDB index count error:", e);
          reject(new Error("Failed to count from index"));
        };
      });
    },
    openCursor: (key?: IDBValidKey, direction?: IDBCursorDirection) => {
      return new Promise<IDBCursorWithValue | null>(async (resolve) => {
        const cursor = await index.openCursor(key, direction);
        resolve(cursor.result);
      });
    },
    first: (key?: IndexedDBFullKey) => {
      return new Promise<T | undefined>((resolve, reject) => {
        const request = index.openCursor(key);
        request.onsuccess = () => resolve(request.result?.value);
        request.onerror = (e) => {
          console.error("IndexedDB index first error:", e);
          reject(new Error("Failed to get first from index"));
        };
      });
    },
    last: (key?: IndexedDBFullKey) => {
      return new Promise<T | undefined>((resolve, reject) => {
        const request = index.openCursor(key, "prev");
        request.onsuccess = () => resolve(request.result?.value);
        request.onerror = (e) => {
          console.error("IndexedDB index last error:", e);
          reject(new Error("Failed to get last from index"));
        };
      });
    },
    iterate: (
      callback: (item: T) => void,
      key?: IndexedDBFullKey,
      direction?: IDBCursorDirection
    ) => {
      return new Promise<void>((resolve, reject) => {
        const request = index.openCursor(key, direction);
        request.onsuccess = () => {
          const cursor = request.result;
          if (cursor) {
            callback(cursor.value);
            cursor.continue();
          } else {
            resolve();
          }
        };
        request.onerror = (e) => {
          console.error("IndexedDB index iterate error:", e);
          reject(new Error("Failed to iterate over index"));
        };
      });
    },
  };
}

function createIDBStorageObjectHandler<T>(
  db: IDBDatabase,
  storeName: string
): IDBStorageObjectHandler<T> {
  return {
    get: (key: IDBValidKey) => getIDBStoreItemByKey(db, storeName, key),
    getAll: (key?: IndexedDBFullKey, count?: number) =>
      getAllIDBStoreRecords(db, storeName, key, count),
    add: (...data: any) => addToIDBStore(db, storeName, ...data),
    bulkAdd: (data: T[]) => bulkAddToIDBStore(db, storeName, data),
    put: (...data: T[]) => putIDBStoreItem(db, storeName, ...data),
    bulkPut: (data: T[]) => bulkPutIDBStoreItems(db, storeName, data),
    count: () => getIDBStoreItemsCount(db, storeName),
    clear: () => clearIDBStore(db, storeName),
    delete: (key: IDBValidKey) => deleteIDBStoreItem(db, storeName, key),
    update: async (key: IDBValidKey, fn: (data: T) => T) =>
      updateIDBStoreItem(db, storeName, key, fn),
    index: (indexName: IDBValidKey, mode: IDBTransactionMode) => {
      const transaction = db.transaction(storeName, mode || "readonly");
      const store = transaction.objectStore(storeName);
      const index = store.index(indexName as string);
      return createIDBStorageIndexHandler(index);
    },
  };
}

export function createIDBHandler<S>(db: IDBDatabase): IDBHandler<S> {
  return {
    db,
    get: <K extends keyof S>(storeName: K, id: IDBValidKey) =>
      getIDBStoreItemByKey(db, storeName as string, id) as Promise<
        S[K] | undefined
      >,
    getAll: <K extends keyof S>(
      storeName: K,
      key?: IndexedDBFullKey,
      count?: number
    ) =>
      getAllIDBStoreRecords(db, storeName as string, key, count) as Promise<
        S[K][]
      >,

    add: <K extends keyof S>(storeName: K, ...data: S[K][]) =>
      addToIDBStore(db, storeName as string, ...data),
    bulkAdd: <K extends keyof S>(storeName: K, data: S[K][]) =>
      bulkAddToIDBStore(db, storeName as string, data),
    bulkPut: <K extends keyof S>(storeName: K, data: S[K][]) =>
      bulkPutIDBStoreItems(db, storeName as string, data),
    count: <K extends keyof S>(storeName: K) =>
      getIDBStoreItemsCount(db, storeName as string),
    objectStore: <K extends keyof S>(storeName: K) =>
      createIDBStorageObjectHandler<S[K]>(db, storeName as string),
    put: <K extends keyof S>(storeName: K, ...data: S[K][]) =>
      putIDBStoreItem(db, storeName as string, ...data),
    delete: <K extends keyof S>(storeName: K, id: IDBValidKey) =>
      deleteIDBStoreItem(db, storeName as string, id),
    update: <K extends keyof S>(
      storeName: K,
      id: IDBValidKey,
      fn: (data: S[K]) => S[K]
    ) => updateIDBStoreItem(db, storeName as string, id, fn),
    clear: <K extends keyof S>(storeName: K) =>
      clearIDBStore(db, storeName as string),
    close: () => {
      db.close();
    },
  };
}

export async function initIDBHandler<S>(
  dbName: string,
  version?: number,
  objectsStore?: IndexedDbObjectStoreInitParams<S>[]
): Promise<IDBHandler<S> | null> {
  const db = await initIDB(dbName, version, objectsStore);
  if (!db) return null;
  return createIDBHandler<S>(db);
}
